import React, {
  forwardRef,
  useState,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Modal,
} from 'antd';

import i18n from '../../config/i18n';
import InputFile from './InputFile';
import Image from './Image';

const styles = {
  inputFile: {
    position: 'absolute',
    visibility: 'hidden',
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '24px',
  },
  button: {
    width: 'calc(50% - 4px)',
  },
  imageWrapper: {
    display: 'flex',
    justifyContent: 'center',
  },
  image: {
    maxHeight: '400px',
    maxWidth: '100%',
  },
  hint: {
    color: '#d52626',
    fontSize: '18px',
    marginTop: '12px',
    textAlign: 'center',
  },
};


const ImageUploader = forwardRef(({
  actions,
  alt,
  children,
  doCrop,
  hint,
  onUpload,
  style,
  title,
}, ref) => {
  const [src, setSrc] = useState('');
  const [file, setFile] = useState();
  const [canPreview, setCanPreview] = useState(false);
  const [crop, setCrop] = useState({});
  const uploadRef = useRef();
  const imageRef = useRef();

  const handleImageLoaded = (image) => {
    const {
      height,
      width,
    } = image;
    const isWidthLarger = width > height;

    imageRef.current = image;

    // Set the crop to the largest square and center it.
    setCrop({
      aspect: 1,
      x: isWidthLarger ? (width - height) / 2 : 0,
      y: isWidthLarger ? 0 : (height - width) / 2,
      width: isWidthLarger ? height : width,
    });

    // Return false when setting crop state in here.
    return false;
  };

  const generateCroppedImage = async () => {
    const image = imageRef.current;
    const {
      x,
      y,
      height,
      width,
    } = crop;
    let croppedImage = null;

    if (image && width && height) {
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const croppedWidth = width * scaleX;
      const croppedHeight = height * scaleY;

      // The maximum width & height (px) of the cropped image.
      // If the cropped image exceeds the max size then compress it.
      const maxWidth = 400;
      const maxHeight = 400;
      const compressScaleX = maxWidth / croppedWidth;
      const compressScaleY = maxHeight / croppedHeight;
      let finalCompressScale = compressScaleX < compressScaleY ? compressScaleX : compressScaleY;
      if (finalCompressScale > 1) { finalCompressScale = 1; }
      const compressWidth = croppedWidth * finalCompressScale;
      const compressHeight = croppedHeight * finalCompressScale;
      canvas.width = compressWidth;
      canvas.height = compressHeight;
      const ctx = canvas.getContext('2d');

      ctx.drawImage(
        image,
        x * scaleX,
        y * scaleY,
        croppedWidth,
        croppedHeight,
        0,
        0,
        compressWidth,
        compressHeight
      );

      croppedImage = await new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            actions.createWarningAlert(i18n.t('cropPhotoFail'));
            reject();
          }
          resolve(new File(
            [blob],
            'avatar.jpg',
            { type: 'image/jpeg' }
          ));
        }, 'image/jpeg');
      });
    } else {
      actions.createWarningAlert(i18n.t('cropPhotoFail'));
    }
    return croppedImage;
  };

  const handleCropChange = (newCrop) => {
    setCrop(newCrop);
  };

  const handleStartSelect = (e) => {
    e.preventDefault();
    uploadRef.current.triggerClick();
    return false;
  };

  const handleSelectFile = (selectedFile) => {
    const reader = new FileReader();
    reader.onload = () => {
      setSrc(reader.result);
      setFile(selectedFile);
      setCanPreview(true);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleUpload = async () => {
    const finalFile = doCrop ? await generateCroppedImage() : file;
    if (finalFile) await uploadRef.current.fileUploadWrapper(finalFile, onUpload);
    setCanPreview(false);
  };

  const handleCancel = (e) => {
    e.preventDefault();
    setCanPreview(false);
  };

  return (
    <div style={style}>
      <Modal
        title={title}
        visible={canPreview}
        // footer={null}
        cancelButtonProps={{
          style: styles.button,
        }}
        okButtonProps={{
          disabled: src === '',
          style: styles.button,
        }}
        cancelText={i18n.t('cancel')}
        okText={i18n.t('confirm')}
        okType="primary"
        onCancel={handleCancel}
        onOk={handleUpload}
      >
        <div style={styles.imageWrapper}>
          {
            doCrop ? (
              <ReactCrop
                src={src}
                crop={crop}
                imageAlt={alt}
                imageStyle={styles.image}
                ruleOfThirds
                onImageLoaded={handleImageLoaded}
                onChange={handleCropChange}
              />
            ) : (
              <Image
                src={src}
                alt={alt}
                style={styles.image}
              />
            )
          }
        </div>
        <div style={styles.hint}>
          {hint}
        </div>
      </Modal>
      <InputFile
        actions={actions}
        ref={(el) => {
          uploadRef.current = el;
          if (ref) ref.current = el;
        }}
        style={styles.inputFile}
        onSelectFile={handleSelectFile}
      />
      {
        children && (
          React.cloneElement(children, { onClick: handleStartSelect })
        )
      }
    </div>
  );
});

ImageUploader.propTypes = {
  actions: PropTypes.shape({
    createWarningAlert: PropTypes.func.isRequired,
  }).isRequired,
  alt: PropTypes.string,
  children: PropTypes.object,
  hint: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]),
  onUpload: PropTypes.func,
  style: PropTypes.object,
  title: PropTypes.string,
  doCrop: PropTypes.bool,
};

ImageUploader.defaultProps = {
  alt: '',
  children: undefined,
  hint: '',
  onUpload: () => Promise.resolve(),
  style: {},
  title: '',
  doCrop: false,
};

export default ImageUploader;



// WEBPACK FOOTER //
// ./src/components/shared/ImageUploader.js